import { auth } from '../Firebase';
import { signInWithEmailAndPassword, createUserWithEmailAndPassword, signOut, sendPasswordResetEmail, updateProfile, sendEmailVerification, AuthError } from 'firebase/auth';
import { apiPublicRequestInstance, apiRequestInstance } from './utils';

const MESSAGES = {
  SIGNUP_SUCCESSFULLY: 'Sing Up Successfully !! Please Verify Your Email.',
  LOGOUT_SUCCESSFULLY: 'Log Out Successfully !! See You Soon.',
  VERIFICATION_EMAIL_WAS_SENT:
    'Verification Email Was Sent. Please Check Your Mailbox.',
  EMAIL_IS_NOT_VERIFIED: 'Email Is Not Verified.',
} as const;

// Helper function to convert Firebase error codes to user-friendly messages
const getFirebaseErrorMessage = (errorCode: string): string => {
  switch (errorCode) {
    case 'auth/user-not-found':
      return 'No user found with this email address.';
    case 'auth/wrong-password':
      return 'Incorrect password. Please try again.';
    case 'auth/email-already-in-use':
      return 'This email is already registered.';
    case 'auth/weak-password':
      return 'Password should be at least 6 characters.';
    case 'auth/invalid-email':
      return 'Invalid email address.';
    case 'auth/operation-not-allowed':
      return 'Email/password accounts are not enabled.';
    case 'auth/too-many-requests':
      return 'Too many failed attempts. Please try again later.';
    case 'auth/network-request-failed':
      return 'Network error. Please check your connection.';
    default:
      return 'An error occurred. Please try again.';
  }
};

/*
  Auth requests
*/
export const getMePromise = async () => apiRequestInstance.get('/me');

export const loginUserPromise = async (email: string, password: string) => {
  try {
    const userCredential = await signInWithEmailAndPassword(auth, email, password);
    const user = userCredential.user;
    
    if (user && !user.emailVerified) {
      // Send the email again
      await sendEmailVerification(user);
      await signOut(auth);
      throw new Error(MESSAGES.EMAIL_IS_NOT_VERIFIED);
    }
    
    return user;
  } catch (error: any) {
    // Transform Firebase errors to match expected format
    if (error.code) {
      // Firebase Auth Error
      const errorMessage = getFirebaseErrorMessage(error.code);
      throw { message: errorMessage };
    }
    // Re-throw other errors as is
    throw { message: error.message || 'An unexpected error occurred' };
  }
};

export const signupUserPromise = async (email: string, password: string, nickname: string) => {
  try {
    const userCredential = await createUserWithEmailAndPassword(auth, email, password);
    const user = userCredential.user;
    
    if (!user) {
      throw new Error('User creation failed');
    }
    
    await updateProfile(user, {
      displayName: nickname,
    });
    await sendEmailVerification(user);
    await signOut(auth);
    
    return {
      message: MESSAGES.SIGNUP_SUCCESSFULLY,
    };
  } catch (error: any) {
    if (error.code) {
      const errorMessage = getFirebaseErrorMessage(error.code);
      throw { message: errorMessage };
    }
    throw { message: error.message || 'An unexpected error occurred' };
  }
};

export const logoutUserPromise = async () => {
  await signOut(auth);
  return {
    message: MESSAGES.LOGOUT_SUCCESSFULLY,
  };
};

export const forgotPasswordPromise = async (email: string) => {
  try {
    await sendPasswordResetEmail(auth, email);
    return {
      message: MESSAGES.VERIFICATION_EMAIL_WAS_SENT,
    };
  } catch (error: any) {
    if (error.code) {
      const errorMessage = getFirebaseErrorMessage(error.code);
      throw { message: errorMessage };
    }
    throw { message: error.message || 'An unexpected error occurred' };
  }
};

/*
  Search requests
*/
export const getAllSearchResultsPromise = (
  platform: string,
  q: string,
  limit: number,
  offset: number
) =>
  apiPublicRequestInstance.get('/search', {
    params: {
      platform,
      q,
      limit,
      offset,
    },
  });

/*
  Following requests
*/
export const getFollowingPromise = (
  platform: string,
  targetType: string,
  targetId: string
) =>
  apiRequestInstance.get(`/following/${platform}/${targetType}/${targetId}`);

export const addFollowingPromise = (
  platform: string,
  targetType: string,
  targetId: string
) =>
  apiRequestInstance.post(
    `/ptt/following/${platform}/${targetType}/${targetId}`,
  );

export const removeFollowingPromise = (
  platform: string,
  targetType: string,
  targetId: string
) =>
  apiRequestInstance.delete(
    `/ptt/following/${platform}/${targetType}/${targetId}`,
  );

/*
  Dashboard requests
*/
export const getDashboardAllFollowingsPromise = (platform: string) =>
  apiRequestInstance.get(`/dashboard/${platform}`);

/*
  Dcard Metric Requests
*/
export const getDcardDBStatsPromise = () =>
  apiRequestInstance.get('/dcard/metric/db-stats');

export const getDcardHotPostsPromise = (
  since: string,
  limit: number,
  offset: number
) =>
  apiRequestInstance.get('/dcard/metric/hot-posts', {
    params: {
      since,
      limit,
      offset,
    },
  });

/*
  PTT Metric Requests
*/
export const getPttDBStatsPromise = () =>
  apiPublicRequestInstance.get('/ptt/metric/db-stats');

export const getAvgPopularTimePromise = () =>
  apiPublicRequestInstance.get('/ptt/metric/popular-time');

export const getHotArticlesPromise = (
  since: string,
  limit: number,
  offset: number
) =>
  apiPublicRequestInstance.get('/ptt/metric/hot-articles', {
    params: {
      since,
      limit,
      offset,
    },
  });

export const getUserGeoDistributionsPromise = () =>
  apiPublicRequestInstance.get('/ptt/metric/user-geo-distributions');